/**
 * city/buildings/villa.js
 * Dựng biệt thự hiện đại đô thị Việt Nam
 */

import * as THREE from 'three';
import { createRng } from '../builder-core.js';

class MethodsHolder {
  // 3.5 BIỆT THỰ HIỆN ĐẠI: khối trên đua ra, cửa kính lớn, ốp đá, lam gỗ, tường rào cổng sắt
  createVilla(options = {}) {
    const villa = new THREE.Group();
    villa.name = 'ModernVilla_VN';
    const rnd = createRng(options.seed ?? 42);
    const white = this.texMat(this.getPlasterTexture('#f5f5f4', 11, 0.2), { roughness: 0.85 });
    const dark = this.mat('#1f2937', { roughness: 0.5 });
    const alu = this.mat('#111827', { roughness: 0.4, metalness: 0.5 });
    const wood = this.mat('#b0622f', { roughness: 0.6 });
    const stone = this.texMat(this._repeat(this.getStoneTexture(), 1, 2), { roughness: 0.95 });

    // TẦNG 1 (x -3.3..2.9, mặt tiền z = 2.5)
    this.box(villa, 6.2, 3.3, 5.4, white, -0.2, 1.65, -0.2);
    const f1z = 2.5;
    this.box(villa, 1.4, 3.3, 0.14, stone, -2.6, 1.65, f1z + 0.07);
    this.box(
      villa,
      0.9,
      2.45,
      0.06,
      this.texMat(this.getWoodPanelTexture('#6b3a1a'), { roughness: 0.6 }),
      -1.35,
      1.225,
      f1z + 0.03
    );
    this.box(villa, 1.1, 0.08, 0.2, alu, -1.35, 2.49, f1z + 0.1);
    this.box(
      villa,
      3.4,
      2.7,
      0.04,
      this.texMat(this.getInteriorTexture('living', 3), {}, true),
      1.0,
      1.4,
      f1z + 0.02,
      null,
      false
    );
    this.box(villa, 3.5, 0.08, 0.1, alu, 1.0, 2.79, f1z + 0.05);
    this.box(villa, 3.5, 0.06, 0.1, alu, 1.0, 0.07, f1z + 0.05);
    for (let i = 0; i <= 4; i++) this.box(villa, 0.06, 2.7, 0.1, alu, -0.7 + i * 0.85, 1.4, f1z + 0.05, null, false);

    // TẦNG 2 ĐUA RA, cửa sổ băng dài, vách lam gỗ, trần đua ốp gỗ có đèn âm
    this.box(villa, 7.0, 3.0, 5.6, white, 0.1, 4.8, 0.3);
    const f2z = 3.1;
    this._addWindow(villa, {
      x: 0.9,
      y: 4.85,
      z: f2z + 0.01,
      w: 4.2,
      h: 1.5,
      frame: '#111827',
      cols: 4,
      sill: false,
      glass: 'home'
    });
    for (let i = 0; i < 12; i++) this.box(villa, 0.08, 2.6, 0.12, wood, -3.2 + i * 0.165, 4.8, f2z + 0.08);
    this.box(villa, 7.0, 0.06, 0.6, wood, 0.1, 3.29, f2z - 0.3);
    [-2.5, -1.0, 0.5, 2.0].forEach((lx) =>
      this.box(villa, 0.12, 0.02, 0.12, this.glow('#fef9c3'), lx, 3.25, f2z - 0.3, null, false)
    );

    // MÁI BẰNG VƯƠN RỘNG + VIỀN ĐEN
    this.box(villa, 7.6, 0.18, 6.2, dark, 0.1, 6.39, 0.3);
    this.box(villa, 7.62, 0.06, 6.22, this.mat('#9ca3af'), 0.1, 6.28, 0.3, null, false);

    // TƯỜNG RÀO ĐÁ + SONG SẮT, CỔNG TRƯỢT, TRỤ CỔNG CÓ ĐÈN
    const fenceZ = 3.6;
    const gateL = -2.1,
      gateR = -0.6;
    const bars = this.mat('#1f2937', { roughness: 0.5, metalness: 0.5 });
    [
      [-3.5, gateL],
      [gateR, 3.7]
    ].forEach(([a, b]) => {
      const len = b - a,
        cx = (a + b) / 2;
      this.box(villa, len, 0.5, 0.22, stone, cx, 0.25, fenceZ);
      this.box(villa, len, 0.04, 0.05, bars, cx, 1.35, fenceZ);
      for (let x = a + 0.1; x < b; x += 0.14) this.box(villa, 0.025, 0.85, 0.025, bars, x, 0.93, fenceZ, null, false);
    });
    [gateL - 0.18, gateR + 0.18, -3.5 - 0.18, 3.7 + 0.18].forEach((px) => {
      this.box(villa, 0.36, 1.6, 0.36, stone, px, 0.8, fenceZ);
      this.box(villa, 0.26, 0.2, 0.26, this.glow('#fef3c7'), px, 1.7, fenceZ, null, false);
    });
    const gateW = gateR - gateL;
    this.box(villa, gateW, 1.35, 0.05, alu, (gateL + gateR) / 2, 0.75, fenceZ + 0.05);
    for (let i = 0; i < 8; i++)
      this.box(villa, gateW, 0.05, 0.02, wood, (gateL + gateR) / 2, 0.2 + i * 0.16, fenceZ + 0.085, null, false);

    // Sân vườn: bụi cây dọc tường rào & lối đi lát đá
    this.box(villa, 7.2, 0.04, 1.0, this.mat('#4d7c0f', { roughness: 0.95 }), 0.1, 0.02, 3.05, null, false);
    this.box(villa, 1.3, 0.05, 1.0, this.mat('#d6d3d1', { roughness: 0.9 }), -1.35, 0.03, 3.05, null, false);
    [
      [-3.0, 3.2],
      [0.4, 3.25],
      [1.6, 3.2],
      [2.9, 3.25]
    ].forEach(([bx, bz]) => {
      for (let i = 0; i < 6; i++) {
        const s = 0.3 + rnd() * 0.25;
        this.box(
          villa,
          s,
          s,
          s,
          this.mat(['#15803d', '#166534', '#22c55e'][Math.floor(rnd() * 3)], { roughness: 0.85 }),
          bx + (rnd() - 0.5) * 0.7,
          s / 2 + rnd() * 0.3,
          bz + (rnd() - 0.5) * 0.3
        );
      }
    });

    return villa;
  }
}

export function registerVillaMethods(proto) {
  Object.getOwnPropertyNames(MethodsHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      proto[name] = MethodsHolder.prototype[name];
    }
  });
}
